import styled from "styled-components";

interface ChatMessageProps {
  children: React.ReactNode;
  timestamp: number;
}

/**
 * User prompt message component
 * Displays the user's message with a timestamp
 */
export const ChatMessage: React.FC<ChatMessageProps> = ({
  children,
  timestamp,
}) => {
  const timeAgo = getTimeAgo(timestamp);

  return (
    <Container>
      <MessageHeader>
        <span />
        <Timestamp>{timeAgo}</Timestamp>
      </MessageHeader>
      <MessageBubble>{children}</MessageBubble>
    </Container>
  );
};

/**
 * Format timestamp to relative time (e.g., "2 minutes ago")
 */
function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) {
    return "just now";
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 12px;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.semanticTextPlaceholder};
`;

const MessageBubble = styled.div`
  background: ${(props) => props.theme.cards2Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: 8px;
  color: ${(props) => props.theme.semanticTextBody};
  padding: 10px 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 13px;
  line-height: 1.5;
`;

export default ChatMessage;
